import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { RadioButtonChecked } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import { v4 as uuidv4 } from 'uuid';
import { checkAnswer } from './helper';
import TimerIcon from 'src/assets/images/quiz/time.svg';
import WarningTimerIcon from 'src/assets/images/quiz/warningTime.svg';
import FinishTimerIcon from 'src/assets/images/quiz/finishTime.svg';

const useStyles = makeStyles((theme) => ({
    mainContainer:{
        display:'flex',
        flexDirection:'column',
        width:'100%'
    },

    timer:{
        display:'flex',
        flexDirection:'row',
        alignItems:'center',
        backgroundColor:'#EBF5DC',
        borderRadius:'100px',
        padding: '10px 20px',
        color:'#39825E',
        
        '& img':{
            width:'20px',
            height:'20px'
        }
    },

    timerSpan:{
        fontSize:'18px',
    },

    wid100Per:{
        width:'100%'
    },

    colDiv:{
        display:'flex',
        flexDirection:'column'
    },

    rowDiv:{
        display:'flex',
        flexDirection:'row'
    },

    alignCenter:{
        alignItems:'center'
    },

    justifyBtw:{
        justifyContent:'space-between'
    },

    quizName:{
        fontSize:'18px',
        fontWeight:600,
        color:'#000',
        textTransform:'capitalize'
    },

    currentQuestionIndex:{
        fontSize:'16px',
        color:'#000000',
        fontWeight:600
    },

    markTag:{
        backgroundColor:'#F3F3F3',
        padding:'7px',
        borderRadius:'5px',
        fontSize:'14px',
        color:'#6D6969',
        margin:'0px 5px'
    },

    questionTypeTag:{
        backgroundColor:'#CCE8F8',
        padding:'7px',
        borderRadius:'5px',
        fontSize:'14px',
        color:'#1A1A1B',
        margin:'0px 5px'
    },

    skipped:{
      backgroundColor:'rgb(255, 244, 229)',
      color:"rgb(102, 60, 0)",
      marginTop:'10px',
      width:'fit-content'
    },

    questionImg:{
        height:'auto',
        maxWidth:'100%',
        margin:'10px 0px'
    },

    answerBtn:{
        background: '#FAFAFA',
        color: '#000000',
        border: '1px solid #EAEAEA',
        borderRadius: '5px',
        padding: '14px 15px',
        transition: 'all 0.4s ease',
        width: '100%',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 400,
        marginTop:'10px',
        marginBottom:'10px',
        textAlign:'left',
        transition: 'all 0.4s ease',

        '&:hover':{
            backgroundColor:'#DFDFDF',
            borderColor:'#EAEAEA'
        }
    },

    selectedAnswer:{
        backgroundColor:'#DDF3FF',
        borderColor:'#36ABEF',

        '&:hover':{
            backgroundColor:'#DDF3FF',
            borderColor:'#36ABEF'
        }
    },

    mainBtn:{
        background:'transparent linear-gradient(270deg, #0F467A 0%, #3D5AF1 100%) 0% 0% no-repeat padding-box',
        backgroundColor:'#0F467A',
        height:'39px',
        textAlign:'center',
        borderRadius:'5px',
        fontSize:'14px',
        color:'#fff',
        fontWeight:600,
        outline:'none',
        border:'none',
        cursor:'button'
    },

    nextBtn:{
        minWidth:'149px',
        width:'149px',
        alignSelf:'flex-end',
        marginTop:'37px',
    },

    skipBtn:{
      backgroundColor:'#e2e2e2 !important',
      background:'#e2e2e2 !important',
      color:'#084671 !important',
      minWidth:'149px',
      width:'149px',
      alignSelf:'flex-end',
      marginTop:'37px',
    }
}));

function Quiz({questions,quizTitle,timer,allowNavigation, customResultPage, onComplete,...props}) {
    const { t, i18n } = props;
    const isRTL = i18n.translator.language;
    const ar = "'Cairo', sans-serif";
    const en = "'Poppins', sans-serif";
    const fontFamily = isRTL === 'ar' ? ar : en;
    const classes = useStyles();

    const [_questions,setQuestions] = useState(questions || []);
    const [incorrectAnswer, setIncorrectAnswer] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showNextQuestionButton, setShowNextQuestionButton] = useState(false);
    const [endQuiz, setEndQuiz] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [buttons, setButtons] = useState({});
    const [correct, setCorrect] = useState([]);
    const [incorrect, setIncorrect] = useState([]);
    const [skipQuestions, setUnanswered] = useState([]);
    const [userInput, setUserInput] = useState([]);
    const [filteredValue, setFilteredValue] = useState('all');
    const [userAttempt, setUserAttempt] = useState(1);
    const [showDefaultResultState, setShowDefaultResult] = useState(true);
    const [answerSelectionTypeState, setAnswerSelectionType] = useState(undefined);
  
    const [totalPoints, setTotalPoints] = useState(null);
    const [correctPoints, setCorrectPoints] = useState(null);
    const [activeQuestion, setActiveQuestion] = useState(_questions[currentQuestionIndex]);
    const [questionSummary, setQuestionSummary] = useState(undefined);
    const [timeRemaining, setTimeRemaining] = useState(timer);
    const [isRunning, setIsRunning] = useState(true);
    
    const staticProps = {
        fontFamily,
        isRTL,
        t
    };

    useEffect(() => {
        setActiveQuestion(_questions[currentQuestionIndex]);
    }, [currentQuestionIndex, _questions]);

    useEffect(() => {
        const { answerSelectionType } = activeQuestion || {};
        // Default single to avoid code breaking due to automatic version upgrade
        setAnswerSelectionType(answerSelectionType || 'single');
    }, [activeQuestion, currentQuestionIndex]);

    useEffect(() => {
        if (endQuiz) {
          setIsRunning(false);
          let totalPointsTemp = 0;
          let correctPointsTemp = 0;
          for (let i = 0; i < _questions.length; i += 1) {
            let point = _questions[i].point || 0;
            if (typeof point === 'string' || point instanceof String) {
              point = parseInt(point, 10);
            }
    
            totalPointsTemp += point;
    
            if (correct.includes(i)) {
              correctPointsTemp += point;
            }
          }
          setTotalPoints(totalPointsTemp);
          setCorrectPoints(correctPointsTemp);
        }
    }, [endQuiz]);

    useEffect(() => {
        setQuestionSummary({
          numberOfQuestions: _questions.length,
          numberOfCorrectAnswers: correct.length,
          numberOfIncorrectAnswers: incorrect.length,
          questions: _questions,
          userInput,
          totalPoints,
          correctPoints,
          timeTaken: timer - timeRemaining,
        });
    }, [totalPoints, correctPoints]);

    // done
    useEffect(() => {
        if (endQuiz && onComplete !== undefined && questionSummary !== undefined) {
          onComplete(questionSummary);
        }
    }, [questionSummary]);

    const getUnansweredQuestions = () => {
        _questions.forEach((question, index) => {
          if (userInput[index] === undefined) {
            setUnanswered((oldArray) => [...oldArray, index]);
          }
        });
    };

    const handleTimeUp = () => {
        setIsRunning(false);
        setEndQuiz(true);
        getUnansweredQuestions();
    };


    const nextQuestion = (currentQuestionIdx) => {
        setIncorrectAnswer(false);
        setIsCorrect(false);
        setShowNextQuestionButton(false);
        setButtons({});
    
        if (currentQuestionIdx + 1 === _questions.length) {
          if (userInput.length !== _questions.length) {
            alert('Quiz is incomplete');
          }
          else {
            setEndQuiz(true);
          }
        } 
        else {
          setCurrentQuestionIndex(currentQuestionIdx + 1);
        }
    };

  const skipQuestion = (currentQuestionIdx) => {
      setIncorrectAnswer(false);
      setIsCorrect(false);
      setShowNextQuestionButton(false);
      setButtons({});

      const copyQuestions = Array.from(_questions);
      const [item] = copyQuestions.splice(currentQuestionIdx, 1);

      // Add the 'skip: true' property to the item
      const updatedItem = { ...item, skip: true };

      // Push the updated item to the end of the array
      copyQuestions.push(updatedItem);
      setQuestions(copyQuestions);
  
      // if (currentQuestionIdx + 1 === questions.length) {
      //   setEndQuiz(true);
      // }
      // else {
      //   setCurrentQuestionIndex(currentQuestionIdx + 1);
      // }
  };
    
    // timer functionality
    useEffect(() => {
        let countdown;
    
        if (timer && isRunning && timeRemaining > 0) {
          countdown = setInterval(() => {
            setTimeRemaining((prevTime) => prevTime - 1);
          }, 1000);
        }
        return () => timer && clearInterval(countdown);
    }, [isRunning, timeRemaining, timer]);

    const renderTags = (answerSelectionType, marks) => {
        return (
          <div className={`${classes.rowDiv} ${classes.alignCenter}`} style={{marginTop:'11px'}}>
            <span className={classes.markTag}>
              {t('quiz.mark',{mark:marks})}
            </span>
            {answerSelectionType === 'single'
              && <span className={classes.questionTypeTag}>{t('quiz.quizLibaray.singleSelectionTagText')}</span>}
            {answerSelectionType === 'multiple'
              && <span className={classes.questionTypeTag}>{t('quiz.quizLibaray.multipleSelectionTagText')}</span>}
          </div>
        );
    };

    const renderAnswers = (question, answerButtons) => {
        const {
          answers, correctAnswer, questionType, questionIndex,
        } = question;
        let { answerSelectionType } = question;
        const onClickAnswer = (index) => checkAnswer(index + 1, correctAnswer, answerSelectionType, answers, {
          userInput,
          userAttempt,
          currentQuestionIndex,
          showNextQuestionButton,
          incorrect,
          correct,
          setButtons,
          setIsCorrect,
          setIncorrectAnswer,
          setCorrect,
          setIncorrect,
          setShowNextQuestionButton,
          setUserInput,
          setUserAttempt,
        });
    
        const checkSelectedAnswer = (index) => {
          if (userInput[questionIndex - 1] === undefined) {
            return false;
          }
          if (answerSelectionType === 'single') {
            return userInput[questionIndex - 1] === index;
          }
          return Array.isArray(userInput[questionIndex - 1]) && userInput[questionIndex - 1].includes(index);
        };
    
        // Default single to avoid code breaking due to automatic version upgrade
        answerSelectionType = answerSelectionType || 'single';
    
        return answers.map((answer, index) => (
          <React.Fragment key={uuidv4()}>
            {(answerButtons[index] !== undefined)
              ? (
                <button
                  type="button"
                //   disabled={answerButtons[index].disabled || false}
                  className={`${classes.answerBtn} ${answerButtons[index].selected ? classes.selectedAnswer : ''}`}
                  onClick={() => onClickAnswer(index)}
                >
                    {questionType === 'text' && 
                    <div className={`${classes.rowDiv} ${classes.alignCenter}`}>
                        {answerButtons[index]?.selected ? <RadioButtonChecked style={{color:'#36ABEF'}}/> : null}
                        <span style={{padding:'0px 10px'}}>{answer}</span>
                    </div>
                    }
                    {questionType === 'photo' && <img src={answer} alt="answer" />}
                </button>
              )
              : (
                <button
                  type="button"
                  onClick={() => onClickAnswer(index)}
                  className={`${classes.answerBtn} ${(allowNavigation && checkSelectedAnswer(index + 1)) ? 'selected' : ''}`}
                >
                  {questionType === 'text' && 
                    <div className={`${classes.rowDiv} ${classes.alignCenter}`}>
                        {answerButtons[index]?.selected ? <RadioButtonChecked style={{color:'#36ABEF'}}/> : null}
                        <span style={{padding:'0px 10px'}}>{answer}</span>
                    </div>
                  }
                  {questionType === 'photo' && <img src={answer} alt="answer" />}
                </button>
              )}
          </React.Fragment>
        ));
    };

    return(
        <div className={classes.mainContainer} style={{fontFamily}}>
            {!endQuiz ?
            <>
            <div className={`${classes.rowDiv} ${classes.alignCenter} ${classes.justifyBtw}`}>
                <span className={classes.quizName} style={{fontFamily}}>{quizTitle}</span>
                {timer ?
                <TimerComponent 
                    {...{
                        ...staticProps,
                        timer,
                        timeRemaining,
                        isRunning
                    }}
                />
                : null}
            </div>
            <Divider style={{margin:'10px 0px'}} />
            </>
            : null}

            {timer && timeRemaining === 0 && isRunning && handleTimeUp()}

            {!endQuiz && (
                <div className={classes.colDiv}>
                    <span className={classes.currentQuestionIndex} style={{fontFamily,marginTop:'20px'}}>{t('quiz.quizLibaray.question',{number: currentQuestionIndex + 1,number2:questions.length})}</span>
                {isRunning ? (
                    <>
                    
                    {activeQuestion
                        && renderTags(
                        answerSelectionTypeState,
                        activeQuestion.point
                    )}

                    {activeQuestion?.skip ? <span className={`${classes.questionTypeTag} ${classes.skipped}`}>{t('quiz.skipped')}</span> : null }

                    <span className={classes.currentQuestionIndex} style={{fontFamily,marginTop:'25px'}}>{activeQuestion && activeQuestion.question}</span>

                    {activeQuestion && activeQuestion.questionPic && (
                        <img src={activeQuestion.questionPic} alt="question" className={classes.questionImg} />
                    )}


                   
                    <div className={`${classes.colDiv} ${classes.wid100Per}`} style={{paddingTop:'27px'}}>
                        {activeQuestion && renderAnswers(activeQuestion, buttons)}
                    </div>
                    {(showNextQuestionButton || allowNavigation) ?
                        <button
                            onClick={() => nextQuestion(currentQuestionIndex)}
                            className={`mainBtn ${classes.nextBtn}`}
                            type="button"
                        >
                            {t('quiz.quizLibaray.nextQuestionBtn')}
                        </button>
                    :
                    !activeQuestion?.skip ?
                    <button
                            onClick={() => skipQuestion(currentQuestionIndex)}
                            className={`mainBtn ${classes.skipBtn}`}
                            type="button"
                        >
                            {t('quiz.quizLibaray.reviewLater')}
                      </button>
                      :
                      null
                    }
                    </>
                ) : 
                null
                }
                </div>
            )}

            {endQuiz && customResultPage !== undefined && customResultPage(questionSummary)}


        </div>
    )
}

export default withTranslation('common')(Quiz);

function TimerComponent({timer,timeRemaining,isRunning,fontFamily,isRTL,finish=false,customCls}) {
    const classes = useStyles()
    const formatTime = (time) => (time < 10 ? '0' : '');
    const displayTime = (time) => {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const seconds = time % 60;

        return `${formatTime(hours)}${hours}:${formatTime(minutes)}${minutes}:${
        formatTime(seconds)
        }${seconds}`;
    };

    const timeLessthanAMinStyle = finish ? {backgroundColor:'#EEF7FD',color:'#36ABEF'} : timeRemaining < 60 ? {backgroundColor:'#FFF1B9',color:'#BE8614'} : {};

    return(
        <div className={`${classes.timer} ${customCls || ''}`} style={{...timeLessthanAMinStyle}}>
            <img src={finish ? FinishTimerIcon : timeRemaining < 60 ? WarningTimerIcon : TimerIcon} alt='timer' />
            <span className={classes.timerSpan} style={{fontFamily,...(isRTL === 'ar' ? {marginRight:'13px'} : {marginLeft:'13px'})}}>{finish || isRunning ? displayTime(timeRemaining) : displayTime(timer - timeRemaining)}</span>
        </div>
    )
}

export {
    TimerComponent
}